import { BookkeeperCompanyIncidenceOptionFragment } from 'generated/resources/graphql'

import type { IncidenceOptionsType } from 'modules/bookkeepers/lib/incidenceOptions'

import { useCompanyIncidenceSettings } from './useCompanyIncidenceSettings'
import { useGetDefaultIncidenceOptions } from './useGetDefaultIncidenceOptions'
import { useGetEmployeeIncidenceOptions } from './useGetEmployeeIncidenceOptions'
import { useGetLeaveIncidenceOptions } from './useGetLeaveIncidenceOptions'
import { useGetPayrollDataIncidenceOptions } from './useGetPayrollDataIncidenceOptions'

export interface IncidenceSettings {
  loading: boolean
  employeeIncidenceOptions: IncidenceOptionsType[]
  defaultIncidenceOptions: IncidenceOptionsType[]
  leaveIncidenceOptions: IncidenceOptionsType[]
  payrollDataIncidenceOptions: IncidenceOptionsType[]
  companyIncidenceSettings: BookkeeperCompanyIncidenceOptionFragment[]
  trackedEventsCount: number
}

const countTrackedEvents = (
  totalEventsCount: number,
  companyIncidenceSettings: BookkeeperCompanyIncidenceOptionFragment[]
): number => {
  let trackedEventsCount = totalEventsCount
  companyIncidenceSettings.forEach((companySetting) => {
    if (!companySetting.trackIncidence) trackedEventsCount--
  })

  return trackedEventsCount
}

export const useGetIncidenceSettings = (
  companyId: number,
  isBookkeeperView: boolean
): IncidenceSettings => {
  const employeeIncidenceOptions = useGetEmployeeIncidenceOptions()
  const defaultIncidenceOptions = useGetDefaultIncidenceOptions()
  const { loading: loadingLeaveIncidences, leaveIncidenceOptions } = useGetLeaveIncidenceOptions(
    companyId,
    isBookkeeperView
  )

  const { loading: loadingCompanySettings, companyIncidenceSettings } = useCompanyIncidenceSettings(
    companyId,
    isBookkeeperView
  )
  const payrollDataIncidenceOptions = useGetPayrollDataIncidenceOptions(
    loadingCompanySettings,
    companyIncidenceSettings
  )
  const totalEventsCount =
    employeeIncidenceOptions.length + defaultIncidenceOptions.length + leaveIncidenceOptions.length

  const trackedEventsCount = countTrackedEvents(totalEventsCount, companyIncidenceSettings)

  return {
    loading: loadingLeaveIncidences || loadingCompanySettings,
    employeeIncidenceOptions,
    defaultIncidenceOptions,
    leaveIncidenceOptions,
    payrollDataIncidenceOptions,
    companyIncidenceSettings,
    trackedEventsCount,
  }
}
